/**
 * Password reset via TOTP.
 *
 * Flow:
 *   1. User enters their email → we start the reset (backend returns a resetId).
 *   2. User enters their authenticator code + new password → backend verifies
 *      the code, sets the new password, and we redirect to /login.
 *
 * We don't send reset emails yet — the CRM doesn't have SMTP/MailChannels
 * wired up. TOTP possession is a strong enough proof-of-identity for our
 * current audience (a small team who all use an authenticator app).
 */
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useMutation } from "@tanstack/react-query"
import { Loader2, ArrowLeft } from "lucide-react"
import { authApi, ApiError } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

type Stage =
  | { kind: "email" }
  | { kind: "verify"; resetId: string }
  | { kind: "done" }

export default function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [stage, setStage] = useState<Stage>({ kind: "email" })
  const [email, setEmail] = useState("")
  const [code, setCode] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirm, setPasswordConfirm] = useState("")
  const [error, setError] = useState<string | null>(null)

  const startMutation = useMutation({
    mutationFn: (e: string) => authApi.forgotPasswordStart(e),
    onSuccess: (data) => {
      setError(null)
      // The backend always returns a resetId (real or decoy) to prevent
      // account enumeration. Verification happens on submit.
      setStage({ kind: "verify", resetId: data.resetId })
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Something went wrong.")
    },
  })

  const completeMutation = useMutation({
    mutationFn: (vars: { resetId: string; code: string; newPassword: string }) =>
      authApi.forgotPasswordComplete(vars.resetId, vars.code, vars.newPassword),
    onSuccess: () => {
      setError(null)
      setStage({ kind: "done" })
      // Bounce back to login after a beat.
      setTimeout(() => navigate("/login", { replace: true }), 2500)
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Something went wrong.")
    },
  })

  return (
    <div className="rc-login-shell grid min-h-screen place-items-center p-4">
      <div className="rc-login-grid" aria-hidden />
      <div className="relative z-10 w-full max-w-md">
        <div className="rc-fade-up mb-10 flex flex-col items-center text-center">
          <span className="rc-login-logo-glow mb-6">
            <img
              src="/brand/rivercity-logo-dark.png"
              srcSet="/brand/rivercity-logo-dark.png 1x, /brand/rivercity-logo-dark@2x.png 2x"
              alt="Rivercity Finance"
              className="h-28 w-auto sm:h-32"
            />
          </span>
          <h1 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">
            Reset your password
          </h1>
          <p className="mt-2 text-sm text-white/60">Verify with your authenticator app</p>
        </div>

        <div className="rc-glass-card rc-fade-up-delay-1 rounded-2xl p-8">
          {stage.kind === "email" && (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                setError(null)
                startMutation.mutate(email)
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoFocus
                />
                <p className="text-xs text-white/50">
                  We&rsquo;ll ask for your authenticator code to verify it&rsquo;s you.
                </p>
              </div>

              {error ? (
                <div className="rounded-md border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-200">
                  {error}
                </div>
              ) : null}

              <Button
                type="submit"
                className="h-11 w-full bg-gradient-to-r from-rc-blue-600 to-cyan-500 text-base font-medium text-white shadow-lg shadow-blue-900/40 hover:from-rc-blue-500 hover:to-cyan-400"
                disabled={startMutation.isPending}
              >
                {startMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Continue"
                )}
              </Button>

              <div className="pt-2 text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1 text-xs text-white/50 transition-colors hover:text-white"
                >
                  <ArrowLeft className="h-3 w-3" />
                  Back to sign in
                </Link>
              </div>
            </form>
          )}

          {stage.kind === "verify" && (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                setError(null)
                if (password.length < 12) {
                  setError("Password must be at least 12 characters.")
                  return
                }
                if (password !== passwordConfirm) {
                  setError("Passwords don&rsquo;t match.")
                  return
                }
                completeMutation.mutate({
                  resetId: stage.resetId,
                  code,
                  newPassword: password,
                })
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="code">Authenticator code</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))}
                  placeholder="6-digit code (or recovery code)"
                  autoFocus
                />
                <p className="text-xs text-white/50">
                  Enter the current 6-digit code from your authenticator app, or a recovery code.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 12 characters"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password-confirm">Confirm new password</Label>
                <Input
                  id="new-password-confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                />
              </div>

              {error ? (
                <div className="rounded-md border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-200">
                  {error}
                </div>
              ) : null}

              <Button
                type="submit"
                className="h-11 w-full bg-gradient-to-r from-rc-blue-600 to-cyan-500 text-base font-medium text-white shadow-lg shadow-blue-900/40 hover:from-rc-blue-500 hover:to-cyan-400"
                disabled={completeMutation.isPending}
              >
                {completeMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Reset password"
                )}
              </Button>

              <div className="pt-2 text-center">
                <button
                  type="button"
                  onClick={() => {
                    setStage({ kind: "email" })
                    setCode("")
                    setPassword("")
                    setPasswordConfirm("")
                    setError(null)
                  }}
                  className="inline-flex items-center gap-1 text-xs text-white/50 transition-colors hover:text-white"
                >
                  <ArrowLeft className="h-3 w-3" />
                  Use a different email
                </button>
              </div>
            </form>
          )}

          {stage.kind === "done" && (
            <div className="space-y-4 text-center">
              <div className="text-emerald-300">
                <p className="text-lg font-medium">Password updated</p>
                <p className="text-sm text-white/60">Redirecting you to sign in&hellip;</p>
              </div>
              <Loader2 className="mx-auto h-5 w-5 animate-spin text-white/60" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
